<template>
  <section id="sl-meal-detail" class="master-section">
    <template v-if="detailIsReady">
      <header>
        <h1 class="title">{{title}}</h1>
      </header>
      <section>
        <div class="form-group" :class="{'has-error': hasNameError}">
          <label class="form-label" for="edit-meal-name">Name</label>
          <input id="edit-meal-name" class="form-input" type="text" v-model="meal.name" ref="name">
        </div>
      </section>
      <section>
        <div class="form-group">
          <label class="form-label">Ingredients</label>
          <table class="table">
            <colgroup>
              <col>
              <col>
              <col>
              <col style="width: 6rem;">
              <col style="width: 2rem;">
            </colgroup>
            <thead>
              <tr>
                <th>Item</th>
                <th>Category</th>
                <th>Stores</th>
                <th colspan="2">Quantity</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="ingr in ingredientList" :key="ingr.id" :data-item="ingr.itemId" :data-id="ingr.id">
                <td class="name-notes">
                  <div class="name">{{ ingr.name }}</div>
                  <div class="notes">{{ ingr.notes }}</div>
                </td>
                <td><span class="cat-name">{{ ingr.category.name }}</span></td>
                <td class="store-list">
                  <span v-for="s in ingr.storeList" :key="s.id" class="label label-rounded sl-store-label">{{ s.name }}</span>
                </td>
                <td class="qty-add text-center">
                  <div class="form-group">
                    <div class="input-group">
                      <button @click.stop="quantityDown(ingr)"
                              class="btn btn-primary input-group-btn hide-xs"
                              :class="{'disabled': ingr.quantityNeeded < 2}">
                        <fa-icon icon="angle-down"></fa-icon>
                      </button>
                      <input type="number" step="1" min="0" v-model="ingr.quantityNeeded" class="form-input text-right">
                      <button @click.stop="quantityUp(ingr)"
                              class="btn btn-primary input-group-btn hide-xs">
                        <fa-icon icon="angle-up"></fa-icon>
                      </button>
                    </div>
                  </div>
                </td>
                <td>
                  <button class="btn btn-error" @click.stop="removeIngredient(ingr)"><fa-icon icon="trash"></fa-icon></button>
                </td>
              </tr>
            </tbody>
            <tbody>
              <tr class="new-item">
                <td colspan="5">
                  <div class="input-group">
                    <span class="input-group-addon is-primary">Add:</span>
                    <input class="form-input" v-model="searchTerm" placeholder="Search for ingredient...">
                    <button @click.stop="searchTerm = ''" class="btn btn-primary input-group-btn"><fa-icon icon="times-circle"></fa-icon></button>
                  </div>
                </td>
              </tr>
            </tbody>
            <tbody>
              <template  v-if="searchBusy">
                <tr>
                  <td colspan="5">
                    <div class="loading loading-xl"></div>
                  </td>
                </tr>
              </template>
              <template v-else>
                <tr v-for="item in filteredResults" :key="item.id">
                  <td class="name-notes">
                    <div class="name">{{ item.name }}</div>
                    <div class="notes">{{ item.notes }}</div>
                  </td>
                  <td><span class="cat-name">{{ item.category.name }}</span></td>
                  <td class="store-list">
                    <span v-for="s in item.storeList" :key="s.id" class="label label-rounded sl-store-label">{{ s.name }}</span>
                  </td>
                  <td colspan="2" class="text-right">
                    <button class="btn btn-success" @click.stop="addIngredient(item)">
                      <fa-icon icon="plus-circle"></fa-icon>
                    </button>
                  </td>
                </tr>
              </template>
            </tbody>
            <tfoot v-if="!isNewMeal">
              <tr>
                <td colspan="5">
                  <div class="table-controls">
                    <button class="btn btn-primary" @click.stop="resetIngredients" :class="{'disabled': !hasIngredientChange}"><span>Reset</span><fa-icon icon="sync"></fa-icon></button>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>
      <section class="sl-form-controls">
        <div class="controls">
          <button class="btn btn-primary btn-cancel" @click.stop="goToMeals"><fa-icon icon="times" /><span>Cancel</span></button>
          <button class="btn btn-error" v-if="!isNewMeal" @click.stop="deleteMeal"><fa-icon icon="trash" /><span>Delete</span></button>
          <button class="btn btn-success" :class="{'disabled': !ingredientList.length}" @click.stop="saveChanges"><fa-icon icon="check" /><span>Save</span></button>
        </div>
      </section>
    </template>
    <template v-else>
      <div class="loading loading-xl"></div>
    </template>
  </section>
</template>

<script>
import _ from 'lodash'

export default {
  name: 'MealDetailView',
  props: {
    id: { type: String, required: false }
  },
  data () {
    return {
      meal: {},
      ingredientItemIdMap: {},
      loaded: false,
      hasNameError: false,
      isNewMeal: true,
      hasIngredientChange: false,
      searchBusy: false,
      searchTerm: '',
      searchResults: []
    }
  },
  computed: {
    detailIsReady () {
      return this.storesAreLoaded && this.itemsAreLoaded && this.mealsAreLoaded
    },
    title () {
      return this.isNewMeal ? 'New Meal' : `Edit Meal`
    },
    ingredientList () {
      return _.sortBy(_.map(this.meal.ingredients, (i) => {
        i.name = `Unknown item (${i.itemId})`
        const item = this.getItemForId(i.itemId)
        if (item) {
          i.name = item.name
          i.notes = item.notes
          i.item = item
          i.storeList = _.map(item.storeIds, sId => this.getStoreForId(sId))
          i.category = this.getCategoryForId(item.categoryId) || { name: 'Unknown' }
        }
        return i
      }), i => `${i.name.toLowerCase()}-${i.category.name.toLowerCase()}`)
    },
    filteredResults () {
      return _.sortBy(_.compact(_.map(this.searchResults, r => {
        if (this.ingredientItemIdMap[`${r.id}`]) {
          return null
        }
        r.storeList = _.map(r.storeIds, sId => this.getStoreForId(sId))
        return r
      })), i => `${i.name.toLowerCase()}-${i.category.name.toLowerCase()}`)
    }
  },
  methods: {
    loadMeal () {
      if (!this.detailIsReady || this.loaded) {
        return
      }
      const newMeal = {
        name: '',
        ingredients: []
      }
      // Make a local copy
      const existingMeal = this.id ? this.getMealForId(this.id) || newMeal : newMeal
      this.meal = _.assign({}, existingMeal)
      this.replaceIngredients(existingMeal.ingredients)
      this.loaded = true
    },
    resetIngredients () {
      if (this.isNewMeal) {
        return
      }
      const existing = this.getMealForId(this.id)
      if (!existing) {
        return
      }
      this.replaceIngredients(existing.ingredients)
    },
    replaceIngredients (ingrList) {
      this.ingredientItemIdMap = {}
      this.$set(this.meal, 'ingredients', _.map(ingrList, i => {
        this.$set(this.ingredientItemIdMap, `${i.itemId}`, true)
        return _.assign({}, i)
      }))
      this.hasIngredientChange = false
    },
    addIngredient (item) {
      this.meal.ingredients.push({
        itemId: item.id,
        quantityNeeded: 1
      })
      this.$set(this.ingredientItemIdMap, `${item.id}`, true)
    },
    removeIngredient (ingredient) {
      this.meal.ingredients.splice(this.meal.ingredients.indexOf(ingredient), 1)
      this.$set(this.ingredientItemIdMap, `${ingredient.itemId}`, false)
      this.hasIngredientChange = true
    },
    quantityUp (ingredient) {
      ingredient.quantityNeeded++
      this.hasIngredientChange = true
    },
    quantityDown (ingredient) {
      ingredient.quantityNeeded = Math.max(ingredient.quantityNeeded - 1, 1)
      this.hasIngredientChange = true
    },
    goToMeals () {
      this.$router.push({ name: 'meals' })
    },
    doSearch () {
      const sText = (this.searchTerm || '').trim()
      this.searchResults = []
      if (sText.length < 3) {
        return
      }
      this.searchBusy = true
      this.searchResults = this.searchItems(sText)
      this.$nextTick(() => { this.searchBusy = false })
    },
    deleteMeal () {
      // TODO: confirmation
    },
    async saveChanges () {
      if (this.hasNameError || !this.meal.name) {
        this.hasNameError = true
        this.$refs['name'].focus()
        return
      }
      const mealId = parseInt(this.id, 10)
      const mealModel = {
        id: mealId || undefined,
        name: this.meal.name.trim(),
        ingredients: _.map(this.ingredientList, i => {
          return {
            id: i.id || undefined,
            mealId: mealId,
            quantityNeeded: i.quantityNeeded,
            itemId: i.itemId
          }
        })
      }
      if (this.isNewMeal) {
        await this.createMeal(mealModel)
      } else {
        await this.updateMeal(mealModel)
      }
      this.$router.push({ name: 'meals' })
    }
  },
  created () {
    this.debouncedSearch = _.debounce(this.doSearch, 300)
  },
  mounted () {
    this.loadMeal()
  },
  watch: {
    detailIsReady (ready) {
      ready && this.loadMeal()
    },
    meal (newMeal) {
      if (this.detailIsReady && !newMeal.id) {
        if (this.id) {
          // Can't find an item for this id... go to the catalog page
          this.$router.push({ name: 'meals' })
        }
        this.isNewMeal = true
        this.setPageMeta({ pageTitle: 'Add Meal' })
        return
      }

      // Have an existing meal, prime settings
      this.isNewMeal = false
      this.setPageMeta({ pageTitle: `Edit ${newMeal.name}` })
    },
    searchTerm () {
      this.debouncedSearch()
    },
    'meal.name' (newName) {
      this.hasNameError = (!newName || !newName.trim())
    }
  }
}
</script>

<style lang="scss">
  @import '../assets/_vars';

  #sl-meal-detail {
    section + section {
      margin-top: 1.5rem;
    }
    table {
      tbody {
        .name-notes {
          .name {

          }
          .notes {
            font-size: 0.8rem;
            font-style: italic;
            color: lighten($primary-color, 20%);
          }
        }
        .cat-name {
          color: lighten($primary-color, 20%);
        }
        .new-item {
          .input-group-addon.is-primary {
            background-color: $dark-color;
            border-color: $darkest-color;
            color: $light-color;
          }
        }
      }
      tfoot {
        .table-controls {
          justify-content: center;
        }
      }
    }
  }
</style>
